import { type PatternSx } from './types';

/**
 * How dense a motif is, as a function of the box it is painted into.
 *
 * ## The problem this exists to solve
 *
 * Every period in this set used to be one absolute number — a 32px grid, a
 * 48px upright, a 26px dot lattice. A single number cannot be right, because
 * the same recipe is painted into boxes that differ by more than ten times:
 * a full-bleed band is 390px wide on a phone and 2560px on a desktop monitor,
 * and `MediaPlate` paints the same recipes into a card frame nearer 320px.
 *
 * A pattern is read as a *proportion*, not as a measurement. The eye asks how
 * many times the motif repeats across the thing it is behind, and the answer
 * decides whether the surface reads as ruling, as texture or as noise. At one
 * fixed period the 128px major grid gave roughly three cells across a phone
 * band — two of them inside the mask, which is a smudge rather than a ruling —
 * and twenty across an ultrawide, which is wallpaper. The motif was constant
 * and the design was not.
 *
 * ## Why a container query and not a breakpoint
 *
 * The thing that decides the right density is the width of the box being
 * painted, which is not the width of the viewport. A card frame on a desktop
 * is narrow and a band on a phone is narrow, and they want the same answer;
 * a viewport media query gets one of the two wrong whichever way it is
 * written. `MediaPlate` therefore needs no special case at all — it is a
 * narrow container, so it is served the narrow step by the same four rules
 * that serve the bands.
 *
 * It also has to be CSS. The site is prerendered, so a density chosen in
 * JavaScript from a `matchMedia` call would either mismatch the prerendered
 * HTML or visibly re-paint on hydration — the same reasoning that keeps
 * `POINTER_HOVER` a media query rather than a hook.
 *
 * ## Why four discrete steps and not a fluid clamp
 *
 * A `clamp()` on `cqi` would scale continuously and never show a seam, and it
 * was the wrong trade twice over. Fractional periods put the rules back on
 * fractional device pixels, which is the moire this module spends `geometry.ts`
 * avoiding; and a continuous scale means no two screens show the same motif,
 * so there is nothing to review and nothing to measure. Four steps of integer
 * periods can be checked by reading the table.
 *
 * The `band` column is the set as it was tuned, so a 1024–1799px band — the
 * desktop baseline, 1440 included — paints exactly what it painted before.
 * The other three columns are that column scaled by roughly 0.63, 0.8 and
 * 1.26 and then rounded to integers, which keeps every rule on a whole pixel
 * at every step.
 */
type Periods = {
  grid: number;
  column: number;
  iso: number;
  dot: number;
  cross: number;
  ring: number;
  diagonal: number;
  hatch: number;
};

/**
 * Minor periods in px. Majors are derived in the recipes (×4, except the
 * diagonals' ×5), so a step never has to restate a number it implies.
 */
const PERIODS: Record<'tight' | 'snug' | 'band' | 'wide', Periods> = {
  /** Card frames, and a band on a phone. */
  tight: { grid: 20, column: 30, iso: 26, dot: 17, cross: 22, ring: 26, diagonal: 12, hatch: 17 },
  /** Tablets, and the wide panel plates that are half a band across. */
  snug: { grid: 26, column: 38, iso: 32, dot: 21, cross: 27, ring: 32, diagonal: 15, hatch: 21 },
  /** The tuned baseline: laptops and desktops up to 1799px. */
  band: { grid: 32, column: 48, iso: 40, dot: 26, cross: 34, ring: 40, diagonal: 18, hatch: 26 },
  /** Large desktop monitors, where the baseline motif starts to read as noise. */
  wide: { grid: 40, column: 60, iso: 50, dot: 33, cross: 43, ring: 50, diagonal: 23, hatch: 33 },
};

/** Every motif's period, as the custom properties the recipes read. */
const vars = (p: Periods) => ({
  '--kabex-pattern-grid': `${p.grid}px`,
  '--kabex-pattern-column': `${p.column}px`,
  '--kabex-pattern-iso': `${p.iso}px`,
  '--kabex-pattern-dot': `${p.dot}px`,
  '--kabex-pattern-cross': `${p.cross}px`,
  '--kabex-pattern-ring': `${p.ring}px`,
  '--kabex-pattern-diagonal': `${p.diagonal}px`,
  '--kabex-pattern-hatch': `${p.hatch}px`,
});

/**
 * The container the steps are measured against.
 *
 * Named rather than anonymous, and the name matters: a `MediaPlate` can sit
 * inside a band that is itself patterned, so there are two of these nested.
 * A query resolves against the nearest ancestor carrying the name, which for
 * the card's own paint is the card's wrapper and for the band's paint is the
 * band's — each layer measures the box it is actually painted into.
 */
export const PATTERN_CONTAINER = 'kabex-band';

const at = (min: number) => `@container ${PATTERN_CONTAINER} (min-width: ${min}px)`;

/**
 * Declared on the layer that *holds* the paint, never on the paint itself: a
 * container query never matches the element that establishes the container,
 * so the two have to be different nodes. `inline-size` and not `size` —
 * nothing here measures height, and inline-size is the weaker containment of
 * the two.
 */
export const patternContainerSx = {
  containerType: 'inline-size',
  containerName: PATTERN_CONTAINER,
} as const;

/**
 * The period table as one `sx` entry. Source order is the cascade here —
 * every query carries the same specificity, so the steps have to be emitted
 * narrowest-first for the widest matching one to win.
 */
export const patternScaleSx: PatternSx = {
  ...vars(PERIODS.tight),
  [at(520)]: vars(PERIODS.snug),
  [at(1024)]: vars(PERIODS.band),
  [at(1800)]: vars(PERIODS.wide),
};
